import type { RoomSaleAdminDetail, RoomSaleAdminEntry } from '@/api/core/room-sales';
import { isApiError } from '@/api/http/errors';
import { useOperatorSession } from '@/auth/runtime';
import {
  Alert,
  AlertDescription,
  Button,
  DataTable,
  type DataTableColumn,
} from '@mgrvtechgroup/admin-ds';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import {
  hasRoomSalesReadAccess,
  roomSaleDetailQuery,
  roomSalesPageQuery,
  type RoomSalesRuntime,
} from './room-sales-queries';

/* eslint-disable @mgrvtechgroup/admin-ds/no-hand-rolled-screen -- This read-only section is composed into the existing property detail shell. */

export const statusLabels: Record<string, string> = {
  not_configured: '미설정',
  midterm: '미드텀 판매',
  long_term: '장기 판매',
  unavailable: '판매 불가',
  provider_unresolved: '외부 처리 확인 중',
  reverse_not_applied: '장기 전환 미적용',
  blocked: '판매 조건 확인 필요',
  provider_mismatch: '외부 판매 정보 불일치',
  current: '현재 상태 확인됨',
};
export const reasonLabels: Record<string, string> = {
  writer_grant_missing: '판매 설정 권한 없음',
  writer_not_ready: '판매 설정 기능 미연결',
  sale_not_configured: '판매 설정 없음',
  sale_not_available: '판매 설정 확인 필요',
  provider_sale_not_available: '외부 판매가격 미적용',
  provider_observation_unavailable: '외부 판매 관측 미확인',
  room_not_selected: '관리 대상 선택 안 됨',
  payment_pinned: '결제 진행 중인 예약 있음',
  no_contiguous_availability: '연속 이용 가능 기간 없음',
  provider_scope_unsupported: '외부 서비스에 연결된 객실이 하나가 아님',
  provider_operation_unresolved: '외부 처리 결과 미확인',
  midterm_reentry_blocked: '미드텀 신규 예약 차단',
  first_date_before_contract_end: '기존 계약 종료일 전 첫 입주일',
  provider_type_mismatch: '외부 판매 유형 불일치',
  channel_not_own_website: '자체 홈페이지 판매 채널 필요',
  no_minimum_stay_window: '최소 이용 기간 미충족',
  refresh_not_observed: '판매 정보를 다시 확인한 기록 없음',
};
const actionLabels: Record<string, string> = {
  configure_native_midterm_sale: '미드텀 판매 설정 가능',
};
export const known = (labels: Record<string, string>, literal: string) =>
  labels[literal] ?? literal;
export const formatDate = (value: string | null) =>
  value
    ? new Intl.DateTimeFormat('ko-KR', { dateStyle: 'medium', timeZone: 'UTC' }).format(
        new Date(`${value}T00:00:00Z`),
      )
    : '미정';
const formatDateTime = (value: string | null) =>
  value
    ? new Intl.DateTimeFormat('ko-KR', { dateStyle: 'medium', timeStyle: 'short' }).format(
        new Date(value),
      )
    : '없음';
const formatPrice = (value: number | null, currency: string | null) =>
  value === null
    ? '현재 판매가격 없음'
    : currency === null
      ? '통화 미확인'
      : `${value.toLocaleString('ko-KR')} ${currency}`;

function failureText(error: unknown, subject: string) {
  if (isApiError(error) && error.status === 401)
    return '로그인이 필요합니다. 로그인 상태를 확인한 뒤 다시 조회해 주세요.';
  if (isApiError(error) && error.status === 403)
    return '현재 계정의 객실 판매 현황 조회 권한을 확인할 수 없습니다.';
  if (isApiError(error) && error.status === 404) return '지점 또는 판매 현황을 찾을 수 없습니다.';
  return `${subject} 정보를 확인하지 못했습니다. 잠시 후 다시 조회해 주세요.`;
}

function SalesFailure({
  error,
  fetching,
  subject,
  retry,
}: {
  error: unknown;
  fetching: boolean;
  subject: string;
  retry: () => void;
}) {
  return (
    <Alert variant="warning" role="status" aria-live="polite">
      <AlertDescription className="flex flex-wrap items-center justify-between gap-3">
        <span>{failureText(error, subject)}</span>
        <Button variant="outline" disabled={fetching} onClick={retry}>
          {fetching ? '조회 중…' : '다시 조회'}
        </Button>
      </AlertDescription>
    </Alert>
  );
}

export function NativeRoomSalesSection({
  readEnabled = true,
  runtime,
  propertyId,
  generation,
  isCurrent,
  onSelectRoom,
}: {
  readEnabled?: boolean;
  runtime: RoomSalesRuntime;
  propertyId: string;
  generation: number;
  isCurrent: () => boolean;
  onSelectRoom?: (roomId: string) => void;
}) {
  const session = useOperatorSession(runtime.session);
  const [cursor, setCursor] = useState<string | null>(null);
  const [previousCursors, setPreviousCursors] = useState<(string | null)[]>([]);
  const [selectedRoomId, setSelectedRoomId] = useState<string | null>(null);
  const allowed = hasRoomSalesReadAccess(runtime, generation) && session.generation === generation;
  const list = useQuery(
    roomSalesPageQuery(runtime, generation, propertyId, cursor, readEnabled && allowed, isCurrent),
  );
  // 같은 쪽을 다시 조회하다 실패하면 직전 data가 남으므로, 오류 때는 그 data로 만든 상세·다음 쪽을 쓰지 않는다.
  const selectedEntry = list.isError
    ? undefined
    : list.data?.items.find((item) => item.room_id.toLowerCase() === selectedRoomId?.toLowerCase());
  const detail = useQuery(
    roomSaleDetailQuery(
      runtime,
      generation,
      propertyId,
      selectedEntry?.room_id ?? null,
      readEnabled && allowed && selectedEntry !== undefined,
      isCurrent,
    ),
  );
  const columns = useMemo<DataTableColumn<RoomSaleAdminEntry>[]>(
    () => [
      {
        key: 'room',
        header: '객실 · 객실 유형',
        sortable: false,
        cell: (item) => (
          <div className="min-w-0">
            <p className="label">{item.room_name}</p>
            <p className="caption text-muted-foreground mt-1">{item.room_type_name}</p>
          </div>
        ),
      },
      {
        key: 'status',
        header: '판매 상태 · 사유',
        sortable: false,
        cell: (item) => (
          <div className="min-w-0">
            <span className="body-sm inline-flex rounded border px-2 py-1">
              {known(statusLabels, item.status)}
            </span>
            <ul className="caption mt-1 space-y-1">
              {item.reasons.length ? (
                item.reasons.map((reason, index) => (
                  <li key={`${reason}-${String(index)}`}>{known(reasonLabels, reason)}</li>
                ))
              ) : (
                <li>추가 사유 없음</li>
              )}
            </ul>
          </div>
        ),
      },
      {
        key: 'first-date',
        header: '첫 입주일',
        sortable: false,
        cell: (item) => <span>{formatDate(item.first_move_in_date)}</span>,
      },
      {
        key: 'price',
        header: '현재 일 가격',
        sortable: false,
        cell: (item) => <span>{formatPrice(item.daily_price_minor, item.price_currency)}</span>,
      },
      {
        key: 'choose',
        header: '상세',
        sortable: false,
        cell: (item) => (
          <Button
            size="sm"
            variant={selectedRoomId === item.room_id ? 'solid' : 'outline'}
            aria-label={`${item.room_name} 판매 현황 상세`}
            aria-pressed={selectedRoomId === item.room_id}
            onClick={() => {
              if (onSelectRoom) onSelectRoom(item.room_id);
              else setSelectedRoomId(item.room_id);
            }}
          >
            {selectedRoomId === item.room_id
              ? '선택됨'
              : onSelectRoom
                ? '가격·판매 설정'
                : '상세 확인'}
          </Button>
        ),
      },
    ],
    [selectedRoomId, onSelectRoom],
  );

  if (runtime.salesReadEnabled !== true) return null;
  return (
    <section
      aria-labelledby="room-sales-heading"
      className="mt-5 flex min-w-0 flex-col gap-3 border-t pt-5"
    >
      <div>
        <h3 id="room-sales-heading" className="heading-xs">
          객실 판매 현황
        </h3>
        <p className="body-sm text-muted-foreground mt-1">
          지점 객실별 중기 판매 상태와 일가격입니다.
        </p>
      </div>
      {/* eslint-disable-next-line no-negated-condition -- Denial is the exceptional branch for this independently gated panel. */}
      {!allowed ? (
        <p role="status" className="body-sm text-muted-foreground">
          현재 계정에서 객실 판매 현황 조회 권한을 확인할 수 없습니다.
        </p>
      ) : (
        <>
          {/* 실패한 쪽은 이전 쪽 행을 보여 주지 않고 오류만 남기며, 이전 쪽으로 돌아갈 단추는 유지한다. */}
          {list.isError ? (
            <SalesFailure
              error={list.error}
              fetching={list.isFetching}
              subject="객실 판매 현황"
              retry={() => void list.refetch()}
            />
          ) : (
            <DataTable
              columns={columns}
              data={
                list.data && cursor === null && list.data.next_cursor === null
                  ? [...list.data.items].sort(
                      (a, b) =>
                        a.room_name.localeCompare(b.room_name, 'ko', { numeric: true }) ||
                        a.room_id.localeCompare(b.room_id),
                    )
                  : (list.data?.items ?? [])
              }
              rowKey={(item) => item.room_id}
              isLoading={list.isLoading}
              empty={{ title: '현재 지점에서 확인되는 객실 판매 현황이 없습니다.' }}
            />
          )}
          <div className="flex flex-wrap items-center gap-2">
            <span className="caption text-muted-foreground">
              {list.isError
                ? '이 페이지를 불러오지 못했습니다'
                : `${String(list.data?.items.length ?? 0)}개 표시 · ${
                    cursor === null && list.data?.next_cursor === null
                      ? '전체 객실 · 객실명 오름차순'
                      : '현재 페이지 · 서버 제공 순서'
                  }`}
            </span>
            <Button
              variant="outline"
              disabled={list.isFetching || previousCursors.length === 0}
              onClick={() => {
                const next = [...previousCursors];
                setPreviousCursors(next.slice(0, -1));
                setCursor(next.at(-1) ?? null);
                setSelectedRoomId(null);
              }}
            >
              이전 페이지
            </Button>
            <Button
              variant="outline"
              disabled={list.isFetching || list.isError || !list.data?.next_cursor}
              onClick={() => {
                if (!list.data?.next_cursor) return;
                setPreviousCursors((current) => [...current, cursor]);
                setCursor(list.data.next_cursor);
                setSelectedRoomId(null);
              }}
            >
              다음 페이지
            </Button>
          </div>
          {selectedEntry && (
            <section
              aria-labelledby="room-sale-detail-heading"
              className="min-w-0 rounded-md border p-4"
            >
              <h4 id="room-sale-detail-heading" className="heading-xs">
                {selectedEntry.room_name} 판매 상세
              </h4>
              {detail.isError ? (
                detail.error instanceof Error &&
                isApiError(detail.error) &&
                detail.error.status === 404 ? (
                  <p role="status" className="body-sm mt-3">
                    이 객실에 판매 설정이 없거나 객실을 찾을 수 없습니다.
                  </p>
                ) : (
                  <div className="mt-3">
                    <SalesFailure
                      error={detail.error}
                      fetching={detail.isFetching}
                      subject="객실 판매 상세"
                      retry={() => void detail.refetch()}
                    />
                  </div>
                )
              ) : detail.isLoading ? (
                <p role="status" className="body-sm text-muted-foreground mt-3">
                  현재 판매 상세를 확인하고 있습니다.
                </p>
              ) : (
                detail.data && <RoomSaleDetail detail={detail.data} />
              )}
            </section>
          )}
        </>
      )}
    </section>
  );
}

function RoomSaleDetail({ detail }: { detail: RoomSaleAdminDetail }) {
  const productChannel = {
    midterm: '미드텀',
    long_term: '장기',
    own_website: '자체 홈페이지',
    external_platform: '외부 플랫폼',
  };
  const outcomes = {
    pending: '처리 대기',
    unknown: '결과 확인 필요',
    applied: '적용 확인',
    not_applied: '미적용 확인',
  };
  return (
    <dl className="body-sm mt-3 grid min-w-0 grid-cols-[minmax(6rem,auto)_1fr] gap-x-3 gap-y-2">
      <dt>상품 · 채널</dt>
      <dd>
        {known(productChannel, detail.product)} · {known(productChannel, detail.channel)}
      </dd>
      <dt>판매 상태</dt>
      <dd>{detail.status === null ? '상태 정보 없음' : known(statusLabels, detail.status)}</dd>
      <dt>사유</dt>
      <dd>
        {detail.reasons.length
          ? detail.reasons.map((reason, index) => (
              <span key={`${reason}-${String(index)}`} className="mr-2">
                {known(reasonLabels, reason)}
              </span>
            ))
          : '추가 사유 없음'}
      </dd>
      <dt>첫 입주일</dt>
      <dd>{formatDate(detail.first_move_in_date)}</dd>
      <dt>현재 일 가격</dt>
      <dd>{formatPrice(detail.daily_price_minor, detail.price_currency)}</dd>
      <dt>끝나지 않은 외부 처리</dt>
      <dd>{detail.pending_outcome === null ? '없음' : known(outcomes, detail.pending_outcome)}</dd>
      <dt>처리 결과를 확인한 시각</dt>
      <dd>{formatDateTime(detail.pending_observed_at)}</dd>
      <dt>미드텀 신규 예약</dt>
      <dd>{detail.midterm_reentry_blocked ? '차단됨' : '차단되지 않음'}</dd>
      <dt>지금 할 수 있는 작업</dt>
      <dd>
        {detail.allowed_actions.length
          ? detail.allowed_actions.map((action, index) => (
              <span key={`${action}-${String(index)}`} className="mr-2">
                {known(
                  {
                    ...actionLabels,
                    transition_to_midterm: '미드텀 전환 가능',
                    transition_to_long_term: '장기 전환 가능',
                    change_first_move_in_date: '첫 입주일 변경 가능',
                  },
                  action,
                )}
              </span>
            ))
          : '지금 할 수 있는 작업 없음'}
      </dd>
    </dl>
  );
}
